const I18N_LOCALE_ALIASES = {
  zh: "zh_CN",
  zh_cn: "zh_CN",
  zh_hans: "zh_CN",
  hans: "zh_CN",
  zh_tw: "zh_TW",
  zh_hant: "zh_TW",
  hant: "zh_TW",
  tw: "zh_TW",
  zh_hk: "zh_TW",
  en: "en_US",
  en_us: "en_US",
  ja: "ja_JP",
  ja_jp: "ja_JP",
  vi: "vi_VN",
  vi_vn: "vi_VN",
};

/**
 * Converts locale variants such as `zh-Hans`, `hans`, and `zh_CN` into a
 * comparable key while preserving unsupported locales.
 */
export function normalizeI18nLocale(locale) {
  const normalizedLocale = String(locale || "")
    .trim()
    .replace(/-/g, "_")
    .toLowerCase();

  if (!normalizedLocale) return "";
  if (I18N_LOCALE_ALIASES[normalizedLocale]) {
    return I18N_LOCALE_ALIASES[normalizedLocale];
  }

  const [language, region] = normalizedLocale.split("_");
  return region ? `${language}_${region.toUpperCase()}` : language;
}

export function parseI18nJson(i18nJson) {
  if (!i18nJson) return null;
  if (typeof i18nJson === "object") return i18nJson;

  try {
    const parsed = JSON.parse(i18nJson);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function getLocalizedValue(values, locale) {
  if (!values || typeof values !== "object") return undefined;

  const targetLocale = normalizeI18nLocale(locale);
  if (!targetLocale) return undefined;

  const matchedKey = Object.keys(values).find(
    key => normalizeI18nLocale(key) === targetLocale,
  );
  const value = matchedKey ? values[matchedKey] : undefined;
  return typeof value === "string" && value.trim() ? value : undefined;
}

/**
 * Resolves one i18nJson field without coupling its fallback to other fields.
 * Fallback order: current locale -> configured default locale -> raw value.
 */
export function getI18nJsonField(i18nJson, field, locale, fallback = "") {
  const content = parseI18nJson(i18nJson);
  if (!content) return fallback;

  const values = content[field];
  return getLocalizedValue(values, locale)
    ?? getLocalizedValue(values, content.defaultLang || content.defaultLocale)
    ?? fallback;
}

/**
 * Returns a copy of an API record with the specified i18nJson fields localized.
 */
export function localizeI18nRecord(record, fields, locale) {
  if (!record || typeof record !== "object") return record;

  const i18nJson = parseI18nJson(record.i18nJson);
  if (!i18nJson) return record;

  return fields.reduce((localizedRecord, field) => ({
    ...localizedRecord,
    [field]: getI18nJsonField(i18nJson, field, locale, record[field]),
  }), { ...record });
}
